import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  ADMIN_PAGE_SIZE,
  formatAdminDate,
  getSupabaseErrorMessage,
} from "./adminConfig";

const R2_PUBLIC_BASE = "https://media.hobbli.com";
const MAX_STOCK_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_STOCK_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function r2PublicUrl(objectKey) {
  if (!objectKey) return "";
  return `${R2_PUBLIC_BASE}/${String(objectKey)
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;
}

async function uploadCanonicalImageToR2(file, itemId) {
  const { data, error } = await supabase.functions.invoke("create-r2-upload", {
    body: {
      contentType: file.type,
      fileSize: file.size,
      purpose: "canonical_image",
      itemId: String(itemId),
    },
  });

  if (error) throw error;
  if (!data?.success || !data?.uploadUrl || !data?.storage?.objectKey) {
    throw new Error(data?.error || "Unable to authorize canonical image upload.");
  }

  const uploadResponse = await fetch(data.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Canonical image upload failed (${uploadResponse.status}).`);
  }

  return {
    provider: data.storage.provider || "r2",
    bucket: data.storage.bucket,
    objectKey: data.storage.objectKey,
    publicUrl: r2PublicUrl(data.storage.objectKey),
  };
}

async function deleteCanonicalImageFromR2(itemId, objectKey = null) {
  const body = {
    resourceType: "canonical_image",
    resourceId: String(itemId),
  };
  if (objectKey) body.objectKey = objectKey;

  const { data, error } = await supabase.functions.invoke("delete-r2-media", { body });
  if (error) throw error;
  if (!data?.success) {
    throw new Error(data?.error || "Unable to delete canonical image from R2.");
  }
  return data;
}

const EMPTY_ITEM = {
  id: null,
  name: "",
  brand: "",
  set_name: "",
  year: "",
  card_number: "",
  category_id: "",
  subcategory_id: "",
  stock_image_storage_provider: null,
  stock_image_storage_bucket: null,
  stock_image_object_key: null,
  additional_details: {},
};

function cleanSearchTerm(value) {
  return String(value || "")
    .trim()
    .replace(/[(),]/g, " ");
}

function normalizeOptionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
}

export default function CanonicalItemManager({ currentUser }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [linkedCounts, setLinkedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [year, setYear] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_ITEM);
  const [additionalDetailsText, setAdditionalDetailsText] = useState("{}");
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [stockImageFile, setStockImageFile] = useState(null);
  const [stockImagePreview, setStockImagePreview] = useState("");

  const [linkedItem, setLinkedItem] = useState(null);
  const [linkedCopies, setLinkedCopies] = useState([]);
  const [loadingCopies, setLoadingCopies] = useState(false);

  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category.name])),
    [categories]
  );

  const subcategoryMap = useMemo(
    () => Object.fromEntries(subcategories.map((subcategory) => [subcategory.id, subcategory.name])),
    [subcategories]
  );

  const visibleSubcategories = useMemo(() => {
    if (!categoryId) return subcategories;
    return subcategories.filter(
      (subcategory) => String(subcategory.category_id) === String(categoryId)
    );
  }, [subcategories, categoryId]);

  const editorSubcategories = useMemo(() => {
    if (!draft.category_id) return subcategories;
    return subcategories.filter(
      (subcategory) => String(subcategory.category_id) === String(draft.category_id)
    );
  }, [subcategories, draft.category_id]);

  const fetchTaxonomy = useCallback(async () => {
    const [categoriesResult, subcategoriesResult] = await Promise.all([
      supabase.from("categories").select("*").order("name"),
      supabase.from("subcategories").select("*").order("name"),
    ]);

    if (categoriesResult.error) {
      console.warn("Unable to load categories:", categoriesResult.error);
    } else {
      setCategories(categoriesResult.data || []);
    }

    if (subcategoriesResult.error) {
      console.warn("Unable to load subcategories:", subcategoriesResult.error);
    } else {
      setSubcategories(subcategoriesResult.data || []);
    }
  }, []);

  const fetchLinkedCounts = useCallback(async (itemIds) => {
    if (!itemIds.length) {
      setLinkedCounts({});
      return;
    }

    const { data, error } = await supabase
      .from("user_items")
      .select("item_id")
      .in("item_id", itemIds);

    if (error) {
      console.warn("Unable to load linked user item counts:", error);
      return;
    }

    const counts = {};
    (data || []).forEach((row) => {
      if (!row.item_id) return;
      counts[row.item_id] = (counts[row.item_id] || 0) + 1;
    });

    setLinkedCounts(counts);
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const from = page * ADMIN_PAGE_SIZE;
    // Fetch one extra row so pagination never needs an exact COUNT(*) over the catalog.
    const to = from + ADMIN_PAGE_SIZE;

    let query = supabase
      .from("items")
      .select("*")
      .order("created_at", { ascending: false })
      .range(from, to);

    const term = cleanSearchTerm(search);
    if (term) {
      query = query.or(
        `name.ilike.%${term}%,brand.ilike.%${term}%,set_name.ilike.%${term}%`
      );
    }

    if (categoryId) query = query.eq("category_id", categoryId);
    if (subcategoryId) query = query.eq("subcategory_id", subcategoryId);
    if (year) query = query.eq("year", Number(year));
    if (cardNumber) query = query.eq("card_number", cardNumber);

    const { data, error } = await query;

    if (error) {
      console.error("Unable to load canonical items:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to load canonical items."));
      setLoading(false);
      return;
    }

    const fetchedRows = data || [];
    const rows = fetchedRows.slice(0, ADMIN_PAGE_SIZE);
    setItems(rows);
    setHasNextPage(fetchedRows.length > ADMIN_PAGE_SIZE);
    await fetchLinkedCounts(rows.map((item) => item.id).filter(Boolean));
    setLoading(false);
  }, [page, search, categoryId, subcategoryId, year, cardNumber, fetchLinkedCounts]);

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItems();
    }, search ? 250 : 0);

    return () => clearTimeout(timer);
  }, [fetchItems, search]);

  useEffect(() => {
    setPage(0);
  }, [search, categoryId, subcategoryId, year, cardNumber]);

  const openEditor = (item = null) => {
    const nextDraft = item
      ? {
          ...EMPTY_ITEM,
          ...item,
          year: item.year ?? "",
          card_number: item.card_number ?? "",
          category_id: item.category_id || "",
          subcategory_id: item.subcategory_id || "",
          additional_details: item.additional_details || {},
        }
      : { ...EMPTY_ITEM };

    setDraft(nextDraft);
    setAdditionalDetailsText(JSON.stringify(nextDraft.additional_details || {}, null, 2));
    setStockImageFile(null);
    setStockImagePreview("");
    setEditorError("");
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving) return;

    if (stockImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(stockImagePreview);
    }

    setStockImageFile(null);
    setStockImagePreview("");
    setEditorOpen(false);
    setEditorError("");
  };

  const handleStockImageFile = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";

    if (!file) return;

    if (!ALLOWED_STOCK_IMAGE_TYPES.has(file.type)) {
      setEditorError("Stock images must be JPG, PNG, or WebP.");
      return;
    }

    if (file.size > MAX_STOCK_IMAGE_BYTES) {
      setEditorError("Stock images must be 10 MB or smaller.");
      return;
    }

    if (stockImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(stockImagePreview);
    }

    setStockImageFile(file);
    setStockImagePreview(URL.createObjectURL(file));
    setEditorError("");
  };

  const clearStockImage = () => {
    if (stockImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(stockImagePreview);
    }

    setStockImageFile(null);
    setStockImagePreview("");
    setDraft((prev) => ({
      ...prev,
      stock_image_storage_provider: null,
      stock_image_storage_bucket: null,
      stock_image_object_key: null,
    }));
  };


  const saveItem = async () => {
    if (!draft.name?.trim()) {
      setEditorError("Name is required.");
      return;
    }

    let parsedDetails = {};
    try {
      parsedDetails = additionalDetailsText.trim()
        ? JSON.parse(additionalDetailsText)
        : {};
    } catch (error) {
      setEditorError(`Additional Details must be valid JSON: ${error.message}`);
      return;
    }

    setSaving(true);
    setEditorError("");

    let itemId = draft.id || null;
    let createdItemId = null;
    let uploadedStockImage = null;

    const basePayload = {
      name: draft.name.trim(),
      brand: draft.brand?.trim() || null,
      set_name: draft.set_name?.trim() || null,
      year: normalizeOptionalNumber(draft.year),
      card_number: normalizeOptionalNumber(draft.card_number),
      category_id: draft.category_id || null,
      subcategory_id: draft.subcategory_id || null,
      additional_details: parsedDetails,
    };

    try {
      // New canonical items must exist before R2 can authorize an upload,
      // because the permanent BIGINT item ID is part of the object namespace.
      if (!itemId) {
        const insertPayload = {
          ...basePayload,
          stock_image_storage_provider: null,
          stock_image_storage_bucket: null,
          stock_image_object_key: null,
          ...(currentUser?.id ? { approved_by: currentUser.id } : {}),
        };

        const { data: created, error: createError } = await supabase
          .from("items")
          .insert([insertPayload])
          .select("*")
          .single();

        if (createError) throw createError;
        itemId = created.id;
        createdItemId = created.id;
      } else {
        const { error: identityError } = await supabase
          .from("items")
          .update(basePayload)
          .eq("id", itemId);

        if (identityError) throw identityError;
      }

      const previousR2ObjectKey =
        draft.stock_image_storage_provider === "r2"
          ? draft.stock_image_object_key
          : null;

      if (stockImageFile) {
        uploadedStockImage = await uploadCanonicalImageToR2(stockImageFile, itemId);

        const { error: imageUpdateError } = await supabase
          .from("items")
          .update({
            stock_image_storage_provider: uploadedStockImage.provider,
            stock_image_storage_bucket: uploadedStockImage.bucket,
            stock_image_object_key: uploadedStockImage.objectKey,
          })
          .eq("id", itemId);

        if (imageUpdateError) {
          try {
            await deleteCanonicalImageFromR2(itemId, uploadedStockImage.objectKey);
          } catch (cleanupError) {
            console.warn("Unable to clean up newly uploaded canonical image:", cleanupError);
          }
          throw imageUpdateError;
        }

        // Only remove the superseded object after the DB points at the new one.
        if (previousR2ObjectKey && previousR2ObjectKey !== uploadedStockImage.objectKey) {
          try {
            await deleteCanonicalImageFromR2(itemId, previousR2ObjectKey);
          } catch (cleanupError) {
            console.warn("Unable to clean up superseded canonical image:", cleanupError);
          }
        }
      } else {
        const removingCurrentR2Image =
          Boolean(previousR2ObjectKey) &&
          !draft.stock_image_object_key;

        if (removingCurrentR2Image) {
          const { error: imageUpdateError } = await supabase
            .from("items")
            .update({
              stock_image_storage_provider: null,
              stock_image_storage_bucket: null,
              stock_image_object_key: null,
            })
            .eq("id", itemId);

          if (imageUpdateError) throw imageUpdateError;

          try {
            await deleteCanonicalImageFromR2(itemId, previousR2ObjectKey);
          } catch (cleanupError) {
            console.warn("Canonical image DB reference was cleared, but R2 cleanup failed:", cleanupError);
          }
        }
      }

      if (stockImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(stockImagePreview);
      }

      setStockImageFile(null);
      setStockImagePreview("");
      setSaving(false);
      setEditorOpen(false);
      await fetchItems();
    } catch (error) {
      // If a brand-new placeholder item could not finish saving, remove only
      // that new DB row. R2 cleanup above is attempted first when applicable.
      if (createdItemId) {
        const { error: rollbackError } = await supabase
          .from("items")
          .delete()
          .eq("id", createdItemId);

        if (rollbackError) {
          console.warn("Unable to roll back failed canonical item creation:", rollbackError);
        }
      }

      console.error("Unable to upload/save canonical stock image:", error);
      setEditorError(
        getSupabaseErrorMessage(
          error,
          error?.message || "Unable to save the canonical item."
        )
      );
      setSaving(false);
    }
  };

  const openLinkedCopies = async (item) => {
    setLinkedItem(item);
    setLinkedCopies([]);
    setLoadingCopies(true);

    const { data, error } = await supabase
      .from("user_items")
      .select("*")
      .eq("item_id", item.id)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Unable to load linked user items:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to load linked user copies."));
    } else {
      setLinkedCopies(data || []);
    }

    setLoadingCopies(false);
  };

  return (
    <div>
      <div className="hadmin-toolbar">
        <input
          className="hadmin-input hadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, brand, or set..."
        />

        <select
          className="hadmin-select"
          style={{ width: 180 }}
          value={categoryId}
          onChange={(event) => {
            setCategoryId(event.target.value);
            setSubcategoryId("");
          }}
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <select
          className="hadmin-select"
          style={{ width: 180 }}
          value={subcategoryId}
          onChange={(event) => setSubcategoryId(event.target.value)}
        >
          <option value="">All subcategories</option>
          {visibleSubcategories.map((subcategory) => (
            <option key={subcategory.id} value={subcategory.id}>
              {subcategory.name}
            </option>
          ))}
        </select>

        <input
          className="hadmin-input"
          style={{ width: 105 }}
          value={year}
          onChange={(event) => setYear(event.target.value)}
          placeholder="Year"
          inputMode="numeric"
        />

        <input
          className="hadmin-input"
          style={{ width: 120 }}
          value={cardNumber}
          onChange={(event) => setCardNumber(event.target.value)}
          placeholder="Card #"
        />

        <button type="button" className="hadmin-btn" onClick={() => openEditor()}>
          + New Canonical Item
        </button>

        <button type="button" className="hadmin-btn-secondary" onClick={fetchItems}>
          Refresh
        </button>
      </div>

      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}

      <section className="hadmin-panel">
        <div className="hadmin-row-between" style={{ marginBottom: 14 }}>
          <div>
            <strong>Canonical catalog</strong>
            <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 3 }}>
              Edit shared identity data here instead of opening Supabase.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="hadmin-empty">Loading canonical catalog...</div>
        ) : !items.length ? (
          <div className="hadmin-empty">No canonical items match the current filters.</div>
        ) : (
          <div className="hadmin-table-wrap">
            <table className="hadmin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Year</th>
                  <th>Brand</th>
                  <th>Set</th>
                  <th>Card #</th>
                  <th>Category</th>
                  <th>Linked Copies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name || "Unnamed item"}</strong>
                      <div className="hadmin-muted" style={{ fontSize: 10, marginTop: 3 }}>
                        {item.id}
                      </div>
                    </td>
                    <td>{item.year ?? "—"}</td>
                    <td>{item.brand || "—"}</td>
                    <td>{item.set_name || "—"}</td>
                    <td>{item.card_number ?? "—"}</td>
                    <td>
                      {categoryMap[item.category_id] || "—"}
                      {item.subcategory_id && (
                        <div className="hadmin-muted">
                          {subcategoryMap[item.subcategory_id] || item.subcategory_id}
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="hadmin-btn-secondary"
                        onClick={() => openLinkedCopies(item)}
                      >
                        {linkedCounts[item.id] || 0}
                      </button>
                    </td>
                    <td>
                      <div className="hadmin-actions">
                        <button
                          type="button"
                          className="hadmin-btn-secondary"
                          onClick={() => openEditor(item)}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="hadmin-pagination">
          <span>
            Page {page + 1}
          </span>
          <div className="hadmin-actions">
            <button
              type="button"
              className="hadmin-btn-secondary"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="hadmin-btn-secondary"
              disabled={!hasNextPage}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {editorOpen && (
        <>
          <div className="hadmin-drawer-backdrop" onClick={closeEditor} />
          <aside className="hadmin-drawer">
            <div className="hadmin-drawer-header">
              <div>
                <h2>{draft.id ? "Edit Canonical Item" : "Create Canonical Item"}</h2>
                <p>{draft.id || "New shared catalog record"}</p>
              </div>
              <button type="button" className="hadmin-icon-btn" onClick={closeEditor}>
                ×
              </button>
            </div>

            <div className="hadmin-drawer-body">
              {editorError && <div className="hadmin-error">{editorError}</div>}

              <div className="hadmin-form-grid">
                <div className="hadmin-form-field full">
                  <label>Name</label>
                  <input
                    className="hadmin-input"
                    value={draft.name}
                    onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Brand</label>
                  <input
                    className="hadmin-input"
                    value={draft.brand || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, brand: event.target.value }))}
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Set Name</label>
                  <input
                    className="hadmin-input"
                    value={draft.set_name || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, set_name: event.target.value }))}
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Year</label>
                  <input
                    className="hadmin-input"
                    value={draft.year}
                    onChange={(event) => setDraft((prev) => ({ ...prev, year: event.target.value }))}
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Card Number</label>
                  <input
                    className="hadmin-input"
                    value={draft.card_number}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, card_number: event.target.value }))
                    }
                  />
                </div>

                <div className="hadmin-form-field">
                  <label>Category</label>
                  <select
                    className="hadmin-select"
                    value={draft.category_id}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        category_id: event.target.value,
                        subcategory_id: "",
                      }))
                    }
                  >
                    <option value="">None</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hadmin-form-field">
                  <label>Subcategory</label>
                  <select
                    className="hadmin-select"
                    value={draft.subcategory_id}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, subcategory_id: event.target.value }))
                    }
                  >
                    <option value="">None</option>
                    {editorSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hadmin-form-field full">
                  <label>Canonical Image</label>

                  <div
                    className="hadmin-actions"
                    style={{
                      marginTop: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      className="hadmin-btn-secondary"
                      style={{ cursor: saving ? "not-allowed" : "pointer" }}
                    >
                      Upload Canonical Image
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleStockImageFile}
                        disabled={saving}
                        style={{ display: "none" }}
                      />
                    </label>

                    {(stockImageFile || draft.stock_image_object_key) && (
                      <button
                        type="button"
                        className="hadmin-btn-secondary"
                        onClick={clearStockImage}
                        disabled={saving}
                      >
                        Remove Image
                      </button>
                    )}

                    {stockImageFile && (
                      <span className="hadmin-muted" style={{ fontSize: 11 }}>
                        {stockImageFile.name} — upload will replace the current image
                      </span>
                    )}
                  </div>

                  {(stockImagePreview || draft.stock_image_object_key) && (
                    <div style={{ marginTop: 10 }}>
                      <img
                        src={
                          stockImagePreview ||
                          r2PublicUrl(draft.stock_image_object_key)
                        }
                        alt="Canonical stock preview"
                        style={{
                          display: "block",
                          maxWidth: 220,
                          maxHeight: 300,
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="hadmin-form-field full">
                  <label>Additional Details (JSON)</label>
                  <textarea
                    className="hadmin-textarea"
                    value={additionalDetailsText}
                    onChange={(event) => setAdditionalDetailsText(event.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="hadmin-drawer-footer">
              <button type="button" className="hadmin-btn-secondary" onClick={closeEditor} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="hadmin-btn" onClick={saveItem} disabled={saving}>
                {saving ? "Saving..." : "Save Canonical Item"}
              </button>
            </div>
          </aside>
        </>
      )}

      {linkedItem && (
        <>
          <div className="hadmin-drawer-backdrop" onClick={() => setLinkedItem(null)} />
          <aside className="hadmin-drawer">
            <div className="hadmin-drawer-header">
              <div>
                <h2>Linked User Copies</h2>
                <p>{linkedItem.name}</p>
              </div>
              <button type="button" className="hadmin-icon-btn" onClick={() => setLinkedItem(null)}>
                ×
              </button>
            </div>

            <div className="hadmin-drawer-body">
              {loadingCopies ? (
                <div className="hadmin-empty">Loading linked copies...</div>
              ) : !linkedCopies.length ? (
                <div className="hadmin-empty">No user items currently link to this canonical record.</div>
              ) : (
                <div className="hadmin-list">
                  {linkedCopies.map((copy) => (
                    <div className="hadmin-list-item" key={copy.id}>
                      <div className="hadmin-row-between">
                        <strong>{copy.user_specific_name || linkedItem.name}</strong>
                        <span className="hadmin-pill">{copy.condition || copy.status || "User item"}</span>
                      </div>
                      <div className="hadmin-muted" style={{ marginTop: 5, fontSize: 11 }}>
                        User: {copy.user_id || "—"}
                      </div>
                      <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 11 }}>
                        Added: {formatAdminDate(copy.created_at)}
                      </div>
                      {copy.estimated_value !== null && copy.estimated_value !== undefined && (
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          Estimated value: ${copy.estimated_value}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
